/**
 * El cliente de la API.
 *
 * **Es el único lugar que hace `fetch`.** Ningún componente arma una URL ni
 * interpreta un error HTTP: reciben datos o reciben un `ApiError` con código.
 * Ese código es contrato con el Worker —`submission_already_processed`,
 * `validation_failed`— y es lo que permite que la pantalla diga algo útil en
 * vez de «error inesperado».
 */

export interface FieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly fields: FieldError[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** El mensaje de un campo concreto, para pintarlo bajo su input. */
  fieldError(name: string): string | undefined {
    return this.fields.find((f) => f.field === name)?.message;
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; details?: FieldError[] };
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    // La sesión viaja en una cookie httpOnly: nunca hay un token en JavaScript,
    // así que un XSS no puede robarla.
    credentials: 'same-origin',
    headers: {},
  };

  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(`/api${path}`, init);
  } catch {
    throw new ApiError('network', 'No hay conexión con el servidor. Revisá tu internet.', 0);
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('Content-Type')?.includes('application/json');
  if (!isJson) {
    if (response.ok) return undefined as T;
    throw new ApiError('unexpected', 'El servidor respondió algo inesperado', response.status);
  }

  const payload = await response.json();

  if (!response.ok) {
    const err = (payload as ErrorBody).error;
    throw new ApiError(
      err?.code ?? 'unknown',
      err?.message ?? 'Algo salió mal',
      response.status,
      Array.isArray(err?.details) ? err.details : [],
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

/** Construye una query string saltándose lo vacío. */
export function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}
